// src/components/AdminUserManagement.tsx - NEW: Extracted for clarity
import React, { useState, useCallback, memo } from 'react';
import { Plus, Edit, Trash, Copy, RotateCcw, ChevronLeft, ChevronRight, Filter, Search, X } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import EditUserModal from './EditUserModal';
import SystemResetModal from './SystemResetModal';
import { useAdminRead } from '../hooks/useAdminRead';
import AdminReadState from './AdminReadState';
import CreateUserModal from './CreateUserModal';
import type { User, UsersResponse } from '../types';

interface AdminUserManagementProps {
  onUserClick: (user: User) => void;
}

const AdminUserManagement: React.FC<AdminUserManagementProps> = memo(({ onUserClick }) => {
  const { showToast, showConfirmationDialog, hideConfirmationDialog, state } = useAppContext();
  const { deleteUserAdmin } = useApi();


  const [page, setPage] = useState(1);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterCustomPricing, setFilterCustomPricing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const pageSize = 25;

  const read = useAdminRead<UsersResponse>(`/api/admin/users/search?page=${page}&size=${pageSize}&filter_custom_pricing=${filterCustomPricing}&search=${encodeURIComponent(searchQuery)}`, 'POST');
  const users = read.data?.users || [];
  const total = read.data?.total || 0;
  const totalPages = read.data?.total_pages || 1;
  const loading = read.loading;
  const loadUsers = read.refresh;

  const handleDeleteUser = useCallback((user: User) => {
    showConfirmationDialog({
      title: 'Delete User',
      message: `Are you sure you want to delete "${user.name}"? This action cannot be undone.`,
      confirmText: 'Delete User',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteUserAdmin(user.api_key);
          showToast({ type: 'success', title: 'User Deleted', message: `User "${user.name}" has been deleted.` });
          loadUsers();
        } catch (error) {
          showToast({ type: 'error', title: 'Delete Failed', message: error instanceof Error ? error.message : 'Failed to delete user.' });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [deleteUserAdmin, showToast, loadUsers, showConfirmationDialog, hideConfirmationDialog]);

  const copyApiKey = useCallback(async (key: string, name: string) => {
    try {
      await navigator.clipboard.writeText(key);
      showToast({ type: 'success', title: 'API Key Copied', message: `API key for ${name} copied.` });
    } catch {
      showToast({ type: 'error', title: 'Copy Failed', message: 'Could not copy API key.' });
    }
  }, [showToast]);

  const toggleCustomPricingFilter = useCallback(() => {
    const newFilter = !filterCustomPricing;
    setFilterCustomPricing(newFilter);
    setPage(1);

  }, [filterCustomPricing, searchQuery, loadUsers]);

  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput);
    setPage(1);

  }, [searchInput, filterCustomPricing, loadUsers]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);

  }, [filterCustomPricing, loadUsers]);

  const handleSearchKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const formatPrice = (price: string | number | null | undefined) => {
    if (price === null || price === undefined) return null;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(4)}`;
  };
  const formatBalance = (balance: string | number | undefined) => {
    if (balance === undefined) return '$0.00';
    const num = typeof balance === 'string' ? parseFloat(balance) : balance;
    return `$${num.toFixed(2)}`;
  };
  const globalPrice = state.schema?.ui_config?.billing?.price_per_contact || '0.02';

  return (
    <div className="user-management">
      <div className="user-management__header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 className="section-title" style={{ margin: 0 }}>User Accounts <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>({total} total)</span></h2>
          <div className="action-buttons">
            <button
              onClick={toggleCustomPricingFilter}
              className={`btn ${filterCustomPricing ? 'btn-primary' : 'btn-secondary'}`}
              title={filterCustomPricing ? 'Showing users with custom pricing' : 'Show users with custom pricing'}
            >
              <Filter size={16} /> {filterCustomPricing ? 'Custom Pricing Only' : 'All Users'}
            </button>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
              <Plus size={16} /> Create User
            </button>
            <button onClick={() => setShowResetModal(true)} className="btn btn-warning">
              <RotateCcw size={16} /> System Reset
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search by name, email, or API key..."
              className="form-input"
              style={{ paddingLeft: '40px', width: '100%' }}
            />
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            {searchQuery && (
              <button
                onClick={clearSearch}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}
                title="Clear search"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <button onClick={handleSearch} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
            <Search size={16} /> Search
          </button>
        </div>
      </div>

      <AdminReadState {...read} hasData={!!read.data} />
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table className="table-premium">
              <thead><tr><th>Name</th><th>Email</th><th>API Key</th><th>Permissions</th><th>Balance</th><th>Price/Contact</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="skeleton-row">{Array.from({ length: 9 }).map((_, j) => (<td key={j}><div className="skeleton-text" /></td>))}</tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#999' }}>
                      {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.api_key} onClick={() => onUserClick(user)} className="clickable-row">
                      <td><span className="font-semibold">{user.name}</span></td>
                      <td><span style={{ fontSize: '13px', color: '#666' }}>{(user as any).email || 'N/A'}</span></td>
                      <td>
                        <div className="api-key-cell" onClick={e => e.stopPropagation()}>
                          <code className="api-key-code" style={{ fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }} title={user.api_key}>
                            {user.api_key}
                          </code>
                          <button className="btn-icon" onClick={() => copyApiKey(user.api_key, user.name)} title="Copy API key"><Copy size={12} /></button>
                        </div>
                      </td>
                      <td>
                        <div className="permission-badge-group">
                          {user.permissions.slice(0, 2).map(p => <span key={p} className="permission-badge">{p}</span>)}
                          {user.permissions.length > 2 && <span className="permission-badge" title={user.permissions.slice(2).join(', ')}>+{user.permissions.length - 2}</span>}
                        </div>
                      </td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{user.balance == null ? 'Not recorded' : formatBalance(user.balance)}</span></td>
                      <td>
                        {user.custom_price_per_contact ? (
                          <span className="custom-price-badge" title={`Custom pricing (Global: $${globalPrice})`}>
                            {formatPrice(user.custom_price_per_contact)}
                          </span>
                        ) : (
                          <span className="text-muted" title="Using global pricing">${globalPrice}</span>
                        )}
                      </td>
                      <td><span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>{user.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td style={{ fontSize: '13px' }}>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="table-actions" onClick={e => e.stopPropagation()}>
                          <button className="btn-icon" onClick={() => { setEditingUser(user); setShowEditModal(true); }} title="Edit user"><Edit size={14} /></button>
                          <button className="btn-icon danger" onClick={() => handleDeleteUser(user)} title="Delete user"><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="card-footer">
            <button className="btn btn-secondary" onClick={() => setPage(page - 1)} disabled={page <= 1}><ChevronLeft size={16} /> Previous</button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <button className="btn btn-secondary" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>Next <ChevronRight size={16} /></button>
          </div>
        )}
      </div>

      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} onCreated={loadUsers} />}
      <EditUserModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} userToEdit={editingUser} onUserUpdated={loadUsers} />
      <SystemResetModal isOpen={showResetModal} onClose={() => setShowResetModal(false)} />
    </div>
  );
});

AdminUserManagement.displayName = 'AdminUserManagement';

export default AdminUserManagement;